<template>
  <div>
  <vs-dialog v-model="isOpen" width="900px" prevent-close @close="close">
    <template #header>
      <h4 class="dialog-title">
        <i class="fal fa-history"></i> ປະຫວັດການເຄື່ອນໄຫວ - {{ branch.name }}
      </h4>
    </template>

    <div class="dialog-content">
      <!-- Filters -->
      <div class="filters">
        <div class="filter-group">
          <label>ເລກບິນ</label>
          <input type="text" class="input" v-model="filters.shipment_number" placeholder="ຄົ້ນຫາເລກບິນ" @keyup.enter="applyFilters">
        </div>
        <div class="filter-group">
          <label>ປະເພດກະເປົ໋າ</label>
          <div class="select">
            <select v-model="filters.wallet_type" @change="applyFilters">
              <option value="FREIGHT">ຄ່າຂົນສົ່ງ</option>
              <option value="COD">ເກັບເງິນປາຍທາງ (COD)</option>
            </select>
          </div>
        </div>
        <div class="filter-group">
          <label>ປະເພດລາຍການ</label>
          <div class="select">
            <select v-model="filters.type" @change="applyFilters">
              <option value="">ທັງໝົດ</option>
              <option value="TOP_UP">ເຕີມເງິນ</option>
              <option value="DEDUCT">ຫັກເງິນ</option>
              <option value="REFUND">ຄືນເງິນ</option>
              <option value="ROLLBACK">ຍ້ອນກັບ</option>
              <option value="TRANSFER_IN">ເຂົ້າ (TRANSFER_IN)</option>
              <option value="TRANSFER_OUT">ອອກ (TRANSFER_OUT)</option>
            </select>
          </div>
        </div>
        <div class="filter-group">
          <label>ຈາກວັນທີ</label>
          <input type="date" class="input" v-model="filters.from_date" @change="applyFilters">
        </div>
        <div class="filter-group">
          <label>ຫາວັນທີ</label>
          <input type="date" class="input" v-model="filters.to_date" @change="applyFilters">
        </div>
      </div>

      <!-- Balance Summary -->
      <div class="balance-summary" v-if="currentBalance !== null">
        <div class="balance-card">
          <span class="balance-label">ຍອດເງິນປັດຈຸບັນ ({{ filters.wallet_type }})</span>
          <span class="balance-value">{{ formatCurrency(currentBalance) }}</span>
        </div>
      </div>

      <!-- Filter-aware money total for the active wallet (degrades to nothing when `totals` absent). -->
      <WalletListTotals :totals="totals" :only="filters.wallet_type.toLowerCase()" />

      <!-- Error Panel -->
      <div v-if="fetchError" class="notification is-warning error-panel">
        <p>{{ fetchError }}</p>
        <p v-if="fetchErrorRequestId" class="error-ref">ref: {{ fetchErrorRequestId }}</p>
        <vs-button flat size="small" @click="fetchTransactions" class="mt-2">
          <i class="fal fa-redo"></i> ລອງໃໝ່
        </vs-button>
      </div>

      <!-- Transactions Table -->
      <vs-table v-else>
        <template #thead>
          <vs-tr>
            <vs-th>ວັນທີ</vs-th>
            <vs-th>ປະເພດ</vs-th>
            <vs-th>ຈຳນວນ</vs-th>
            <vs-th>ຍອດຄົງເຫລືອ</vs-th>
            <vs-th>ເລກອ້າງອີງ</vs-th>
            <vs-th>ເລກທະນາຄານ</vs-th>
            <vs-th>ເລກບິນ</vs-th>
            <vs-th>ສະຖານະສຳເລັດ</vs-th>
            <vs-th>ເສັ້ນທາງ</vs-th>
            <vs-th>ຜູ້ສົ່ງ</vs-th>
            <vs-th>ເບີຜູ້ສົ່ງ</vs-th>
            <vs-th>ຜູ້ຮັບ</vs-th>
            <vs-th>ເບີຜູ້ຮັບ</vs-th>
            <vs-th>ປະເພດຈ່າຍ</vs-th>
            <vs-th>Pre-Order</vs-th>
            <vs-th>ໝາຍເຫດ</vs-th>
            <vs-th>ສະລິບ</vs-th>
            <vs-th>ຈັດການ</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr v-for="tx in transactions" :key="tx.id">
            <vs-td>{{ formatDate(tx.created_at) }}</vs-td>
            <vs-td>
              <span :class="['tx-type', isCredit(tx) ? 'credit' : 'debit']">
                {{ isCredit(tx) ? 'ເຂົ້າ' : 'ອອກ' }}
              </span>
            </vs-td>
            <vs-td>
              <span :class="['tx-amount', isCredit(tx) ? 'credit' : 'debit']">
                {{ isCredit(tx) ? '+' : '-' }}{{ formatCurrency(tx.amount) }}
              </span>
            </vs-td>
            <vs-td>{{ formatCurrency(tx.balance_after) }}</vs-td>
            <vs-td>
              <span class="reference">{{ tx.ref_no }}</span>
            </vs-td>
            <vs-td>
              <span v-if="tx.bank_ref_no" class="reference">{{ tx.bank_ref_no }}</span>
              <span v-else class="no-data">-</span>
            </vs-td>
            <vs-td>
              <span v-if="tx.shipment_number" class="reference">{{ tx.shipment_number }}</span>
              <span v-else class="no-data">-</span>
            </vs-td>
            <vs-td>
              <span v-if="tx.shipment_success_type" :class="['success-type-badge', tx.shipment_success_type]">
                {{ getSuccessTypeLabel(tx.shipment_success_type) }}
              </span>
              <span v-else class="no-data">-</span>
            </vs-td>
            <vs-td>{{ routeLabel(tx) }}</vs-td>
            <vs-td>{{ tx.sender_customer_name }}</vs-td>
            <vs-td>{{ tx.sender_customer_tel }}</vs-td>
            <vs-td>{{ tx.receiver_customer_name }}</vs-td>
            <vs-td>{{ tx.receiver_customer_tel }}</vs-td>
            <vs-td>{{ payTypeLabel(tx.shipment_pay_type) }}</vs-td>
            <vs-td>
              <span v-if="preOrderLabel(tx.shipment_is_pre_order)" class="preorder-badge">
                {{ preOrderLabel(tx.shipment_is_pre_order) }}
              </span>
            </vs-td>
            <vs-td>
              <span class="notes" :title="tx.notes">
                {{ truncate(tx.notes, 40) }}
              </span>
            </vs-td>
            <vs-td>
              <vs-button
                  v-if="hasSlip(tx)"
                  flat
                  icon
                  size="small"
                  @click="viewSlip(tx)"
                  :loading="loadingSlipId === tx.id"
                  title="ເບິ່ງສະລິບ"
              >
                <i class="fal fa-image" style="color: #17a2b8"></i>
              </vs-button>
              <span v-else class="no-slip">-</span>
            </vs-td>
            <vs-td>
              <span v-if="tx.is_reversed" class="reversed-badge">ກັບຄືນແລ້ວ</span>
              <!-- Reverse/rollback: SuperAdmin/accountingmanager only. UI-only gate — Go still
                   authorizes reverse for the view-only accounting roles (not enforced at Go). -->
              <vs-button
                  v-else-if="canReverse(tx) && $can(['SuperAdmin', 'accountingmanager'])"
                  flat
                  icon
                  size="small"
                  @click="openReverseDialog(tx)"
                  title="ກັບຄືນລາຍການ"
              >
                <i class="fal fa-undo" style="color: #dc3545"></i>
              </vs-button>
              <span v-else class="no-data">-</span>
            </vs-td>
          </vs-tr>
        </template>
        <template #notFound>
          <div class="not-found">ບໍ່ພົບລາຍການ</div>
        </template>
      </vs-table>

      <vs-pagination
          v-if="totalPages > 1"
          v-model="currentPage"
          :length="totalPages"
          @input="fetchTransactions"
      />
    </div>

    <template #footer>
      <div class="dialog-footer">
        <vs-button flat @click="exportTransactions" :disabled="transactions.length === 0 || isExporting" :loading="isExporting">
          <i class="fal fa-download"></i> ດາວໂຫລດ Excel
        </vs-button>
        <vs-button @click="close">ປິດ</vs-button>
      </div>
    </template>
  </vs-dialog>

  <!-- Slip Image Viewer -->
  <vs-dialog v-model="showSlipViewer" width="600px">
    <template #header>
      <h4 class="dialog-title">
        <i class="fal fa-image"></i> ສະລິບການໂອນ
      </h4>
    </template>
    <div class="slip-viewer-content">
      <img v-if="slipViewerUrl" :src="slipViewerUrl" alt="ສະລິບ">
    </div>
    <template #footer>
      <div class="dialog-footer">
        <vs-button flat @click="showSlipViewer = false">ປິດ</vs-button>
      </div>
    </template>
  </vs-dialog>

  <!-- Reverse Transaction Dialog -->
  <ReverseTransactionDialog
      v-if="reverseTarget"
      :transaction="reverseTarget"
      @close="reverseTarget = null"
      @reversed="onReversed"
  />
  </div>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import { formatCurrency, formatErrorMessage, formatDate, truncate, isCredit } from '@/services/walletFormatters';
import { payTypeLabel, preOrderLabel, routeLabel } from '@/services/walletTransactionLabels';
import ReverseTransactionDialog from './ReverseTransaction.vue';
import WalletListTotals from '../WalletListTotals.vue';

export default {
  name: 'TransactionHistoryDialog',
  components: { ReverseTransactionDialog, WalletListTotals },
  props: {
    branch: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isOpen: true,
      isLoading: false,
      isExporting: false,
      fetchError: null,
      fetchErrorRequestId: null,
      transactions: [],
      totals: null,
      currentBalance: null,
      currentPage: 1,
      totalPages: 1,
      perPage: 15,
      abortController: null,
      showSlipViewer: false,
      slipViewerUrl: null,
      loadingSlipId: null,
      reverseTarget: null,
      filters: {
        wallet_type: 'FREIGHT',
        type: '',
        shipment_number: '',
        from_date: '',
        to_date: '',
      },
    };
  },
  created() {
    this.setDefaultDates();
    this.fetchTransactions();
    this.fetchCurrentBalance();
  },
  beforeDestroy() {
    if (this.slipViewerUrl) {
      URL.revokeObjectURL(this.slipViewerUrl);
      this.slipViewerUrl = null;
    }
  },
  methods: {
    canReverse(tx) {
      // The server decides — the client CANNOT. Telling a reversible hand-typed cash slip from a
      // refused bank top-up needs top_up_type (not on the wire) and the hw_top_up_requests link
      // (a DB lookup). An absent is_reversible means "this endpoint did not compute it", so the
      // strict === true keeps the button hidden rather than offering a guaranteed 400.
      return tx.is_reversible === true;
    },
    openReverseDialog(tx) {
      this.reverseTarget = tx;
    },
    onReversed() {
      this.reverseTarget = null;
      this.fetchTransactions();
      this.fetchCurrentBalance();
    },
    close() {
      if (this.abortController) {
        this.abortController.abort();
      }
      if (this.slipViewerUrl) {
        URL.revokeObjectURL(this.slipViewerUrl);
      }
      this.isOpen = false;
      this.$emit('close');
    },
    setDefaultDates() {
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      this.filters.to_date = today.toISOString().split('T')[0];
      this.filters.from_date = thirtyDaysAgo.toISOString().split('T')[0];
    },
    applyFilters() {
      // A filter change must start at page 1 so the first-page-only totals refresh for the new
      // filter (and switching wallet_type also re-points which bucket the totals card shows).
      this.currentPage = 1;
      this.fetchTransactions();
    },
    async fetchTransactions() {
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.isLoading = true;
      this.fetchError = null;
      this.fetchErrorRequestId = null;
      try {
        const params = {
          wallet_type: this.filters.wallet_type,
          page: this.currentPage,
          page_size: this.perPage,
        };

        if (this.filters.type) params.transaction_type = this.filters.type;
        if (this.filters.shipment_number) params.shipment_number = this.filters.shipment_number;
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.to_date) params.to_date = this.filters.to_date;

        const response = await walletService.getTransactions(
          this.$axios, this.branch.id, params, this.abortController.signal
        );

        this.transactions = response.data || [];
        this.totalPages = response.meta?.total_pages || 1;
        // Totals ride only on page 1 (filter-aware over the whole set); retain them while paging.
        if (this.currentPage === 1) {
          this.totals = response.totals || null;
        }

        this.fetchCurrentBalance();
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        console.error('Fetch transactions error:', error.response?.data || error);
        const { message, requestId } = extractErrorInfo(error);
        this.fetchError = message || 'ບໍ່ສາມາດໂຫລດຂໍ້ມູນໄດ້';
        this.fetchErrorRequestId = requestId;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchCurrentBalance() {
      try {
        // F7/R6: ALWAYS re-fetch the authoritative Go balance — never short-circuit on the immutable
        // `branch.wallets` prop snapshot. onReversed() calls this after a reversal, so reading the
        // open-time snapshot would show a stale balance; Go is the source of truth for the card.
        const response = await walletService.getWallets(this.$axios, this.branch.id);
        const freightWallet = response.data?.freight_wallet;
        const codWallet = response.data?.cod_wallet;

        if (this.filters.wallet_type === 'FREIGHT') {
          this.currentBalance = freightWallet?.balance || 0;
        } else {
          this.currentBalance = codWallet?.balance || 0;
        }
      } catch (error) {
        console.error('Failed to fetch balance:', error);
      }
    },
    formatDate,
    formatCurrency(amount) {
      return formatCurrency(amount);
    },
    truncate,
    isCredit,
    payTypeLabel,
    preOrderLabel,
    routeLabel,
    hasSlip(tx) {
      // Manual top-ups and inter-wallet transfers are the only transactions that carry a
      // slip image (bank-QR top-ups have none). Keyed on reference_type to match
      // All-Transactions.vue.
      return tx.reference_type === 'MANUAL_TOPUP' || tx.reference_type === 'INTER_WALLET_TRANSFER';
    },
    async viewSlip(tx) {
      // Revoke previous URL to avoid memory leaks
      if (this.slipViewerUrl) {
        URL.revokeObjectURL(this.slipViewerUrl);
        this.slipViewerUrl = null;
      }

      this.loadingSlipId = tx.id;
      try {
        const result = await walletService.getTransactionSlip(this.$axios, tx.id);

        // 404 — no slip attached (QR top-ups)
        if (!result) {
          this.$notification.OpenNotification_Error(
            'top-right', 'warning', 3000, 'ບໍ່ມີສະລິບແນບ'
          );
          return;
        }

        // PDF — open in new browser tab
        if (result.contentType.includes('application/pdf')) {
          window.open(result.url, '_blank');
          // Revoke after a short delay to let the browser load it
          setTimeout(() => URL.revokeObjectURL(result.url), 5000);
          return;
        }

        // Image — show in modal
        this.slipViewerUrl = result.url;
        this.showSlipViewer = true;
      } catch (error) {
        this.$notification.OpenNotification_Error(
          'top-right', 'danger', 3000,
          formatErrorMessage(error, 'ບໍ່ສາມາດໂຫລດສະລິບໄດ້')
        );
      } finally {
        this.loadingSlipId = null;
      }
    },
    getSuccessTypeLabel(type) {
      const labels = {
        normal: 'ສຳເລັດ',
        split: 'ຈັດສົ່ງແບບແຍກ',
        returned: 'ຕີກັບສຳເລັດ',
        returned_split: 'ຈັດສົ່ງຕີກັບສຳເລັດແຍກ',
      };
      return labels[type] || type;
    },
    async exportTransactions() {
      this.isExporting = true;
      try {
        const params = {
          wallet_type: this.filters.wallet_type,
          format: 'xlsx',
        };

        if (this.filters.type) params.transaction_type = this.filters.type;
        if (this.filters.shipment_number) params.shipment_number = this.filters.shipment_number;
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.to_date) params.to_date = this.filters.to_date;

        await walletService.exportBranchTransactions(this.$axios, this.branch.id, params);
        this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'success', 3000);
      } catch (error) {
        console.error('Export error:', error);
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'ບໍ່ສາມາດດາວໂຫລດໄດ້'));
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 18px;
  font-weight: 600;

  i {
    margin-right: 8px;
    color: #17a2b8;
  }
}

.dialog-content {
  padding: 20px;
  min-height: 400px;
}

.filters {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  .filter-group {
    flex: 1;
    min-width: 150px;

    label {
      display: block;
      margin-bottom: 6px;
      font-size: 13px;
      font-weight: 500;
      color: #666;
    }

    .input,
    .select select {
      width: 100%;
    }
  }
}

.balance-summary {
  margin-bottom: 20px;

  .balance-card {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    padding: 12px 24px;
    border-radius: 8px;

    .balance-label {
      font-size: 14px;
      opacity: 0.9;
    }

    .balance-value {
      font-size: 20px;
      font-weight: 700;
    }
  }
}

.tx-type {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;

  &.credit {
    background: #d4edda;
    color: #155724;
  }

  &.debit {
    background: #f8d7da;
    color: #721c24;
  }
}

.tx-amount {
  font-weight: 600;

  &.credit {
    color: #28a745;
  }

  &.debit {
    color: #dc3545;
  }
}

.reference {
  font-size: 12px;
  color: #666;
  font-family: monospace;
}

.success-type-badge {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  vertical-align: middle;

  &.normal {
    background: #d4edda;
    color: #155724;
  }

  &.split {
    background: #cce5ff;
    color: #004085;
  }

  &.returned {
    background: #fff3cd;
    color: #856404;
  }

  &.returned_split {
    background: #f8d7da;
    color: #721c24;
  }
}

.notes {
  font-size: 13px;
  color: #666;
}

.error-panel {
  margin-bottom: 16px;

  .error-ref {
    font-size: 12px;
    color: #856404;
    margin-top: 4px;
  }

  .mt-2 {
    margin-top: 0.5rem;
  }
}

.not-found {
  text-align: center;
  padding: 40px;
  color: #999;
}

.no-data {
  color: #999;
}

.reversed-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #e2e3e5;
  color: #6c757d;
}

.preorder-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #e7f1ff;
  color: #0d6efd;
}

.no-slip {
  color: #999;
}

.slip-viewer-content {
  text-align: center;
  padding: 16px;

  img {
    max-width: 100%;
    max-height: 500px;
    border-radius: 8px;
    border: 1px solid #ddd;
  }
}

.dialog-footer {
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}
</style>
